import * as yup from 'yup';
import moment from 'moment';
import { FORM_ERROR_MESSAGES } from 'internalConstants';
import { IDENTITY_TYPES, NAME_CHANGE_DOC_TYPES } from 'arnConstants';

const FORM_ERROR_MESSAGES_DOB_FIELD = 'Please enter your Date of Birth';
const FORM_ERROR_MESSAGE_ACCEPT_DECLARATION = 'You must accept the declaration';

const MESSAGE_REGISTRATION_NUMBER = 'Please enter the registration number';

const minDate = '1900-01-01';
const today = moment();
const numberOnlyRegex = /^([^0-9]*)$/;
const lettersOnlyRegex = /^\D+$/;
const passportNumberRegex = /[a-zA-Z]{1,2}\d{7}$/;
const immiCardNumberRegex = /[a-zA-Z]{3}\d{6}$/;
const specialCharsRegex = /[!@#$%^&*()_+=~`\[\]{};:"\\|,.<>\/?]+/; //eslint-disable-line
const isFieldValidationRequired = (isInAustralia, isAuPermanentResident) => {
  return (isInAustralia === 'yes' || (isInAustralia === 'no' && isAuPermanentResident === 'yes'));
};
const containsSpecialCharacters = (string) => {
  return specialCharsRegex.test(string);
};

const isValidAddess = (addressComponents, lookUpAddress) => {
  if (!addressComponents.cityOrSuburb || !addressComponents.state || !addressComponents.postcode || !lookUpAddress) {
    return false;
  }
  if ((addressComponents.houseOrBuildingNumber != undefined && !lookUpAddress.toLowerCase().includes(addressComponents.houseOrBuildingNumber.toLowerCase()))
    || (addressComponents.cityOrSuburb != undefined && !lookUpAddress.toLowerCase().includes(addressComponents.cityOrSuburb.toLowerCase()))
    || (addressComponents.state != undefined && !lookUpAddress.toLowerCase().includes(addressComponents.state.toLowerCase()))
    || (addressComponents.postcode != undefined && !lookUpAddress.toLowerCase().includes(addressComponents.postcode.toLowerCase()))) {
    return false;
  }
  return true;
};
export const personalDetailsValidationSchema = yup.object().shape({
  title: yup.string()
    .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD),
  firstName: yup.string()
    .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
    .matches(lettersOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
    .test('Should not contain special character', (value, context) => {
      const path = context.path;
      return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
    }),
  otherGivenNames: yup.string()
    .matches(lettersOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
    .test('Should not contain special character', (value, context) => {
      const path = context.path;
      return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
    }),
  lastName: yup.string()
    .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
    .matches(lettersOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
    .test('Should not contain special character', (value, context) => {
      const path = context.path;
      return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
    }),
  dateOfBirth: yup.string()
    .test('date-value-test', (value, context) => {
      const dateValue = moment(value, 'YYYY-MM-DD', true);
      const path = context.path;
      if (!value) {
        return context.createError({ path, message: FORM_ERROR_MESSAGES.REQUIRED_FIELD });
      }
      if (moment(value).isBefore(minDate, 'year') || moment(value).isAfter(today)) {
        return context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY });
      }
      return dateValue.isValid() || context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY });
    }).required(FORM_ERROR_MESSAGES.REQUIRED_FIELD),
  sex: yup.string()
    .required(FORM_ERROR_MESSAGES.REQUIRED_OPTION),
  nationality: yup.string()
    .required(FORM_ERROR_MESSAGES.REQUIRED_OPTION)
});

export const personalDetailsMyGovIdPathValidationSchema = yup.object().shape({
  title: yup.string()
    .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD),
  sex: yup.string()
    .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD),
  nationality: yup.string()
    .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
});

export const addressesAndContactDetailsValidationSchema = yup.object().shape({
  address: yup.object().shape({
    country: yup.string()
      .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD),
    unitNumber: yup.string()
      .when('quickLookup', {
        is: false,
        then: yup.string()
          .matches(/\d/, FORM_ERROR_MESSAGES.INVALID_ENTRY)
          .test('Should not contain special character', (value, context) => {
            const path = context.path;
            return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
          })
      }),
    houseOrBuildingNumber: yup.string()
      .when('quickLookup', {
        is: false,
        then: yup.string()
          .matches(/^(\d+-?)+\d+|\d$/, FORM_ERROR_MESSAGES.INVALID_ENTRY)
          .test('Should not contain special character', (value, context) => {
            const path = context.path;
            return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
          })
      }),
    buildingName: yup.string()
      .when('quickLookup', {
        is: false,
        then: yup.string()
          .matches(lettersOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
          .test('Should not contain special character', (value, context) => {
            const path = context.path;
            return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
          }),
      }),
    streetName: yup.string()
      .when('quickLookup', {
        is: false,
        then: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
          .matches(numberOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
          .test('Should not contain special character', (value, context) => {
            const path = context.path;
            return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
          }),
      }),
    cityOrSuburb: yup.string()
      .when('quickLookup', {
        is: false,
        then: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
          .matches(numberOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
          .test('Should not contain special character', (value, context) => {
            const path = context.path;
            return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
          }),
      }),
    state: yup.string()
      .when('quickLookup', {
        is: false,
        then: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD),
      }),
    postcode: yup.string()
      .when('quickLookup', {
        is: false,
        then: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
          .matches(/^\d+$/, FORM_ERROR_MESSAGES.INVALID_ENTRY),
      }),
    addressLookup: yup.string()
      .when(['country', 'quickLookup'], {
        is: (country, quickLookup) => {
          return country === 'Australia' && quickLookup;
        },
        then: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
          .test('valid address test', (value, context) => {
            const path = context.path;
            const parentValues = context.parent;
            return !isValidAddess(parentValues, value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
          })
      })
  }),
  postalAddress: yup.object()
    .when('isPostalAddressDifferent', {
      is: 'yes',
      then: yup.object().shape({
        country: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD),
        poBox: yup.string()
          .when('quickLookup', {
            is: false,
            then: yup.string()
              .matches(/\d/, FORM_ERROR_MESSAGES.INVALID_ENTRY)
          }),

        unitNumber: yup.string()
          .when('quickLookup', {
            is: false,
            then: yup.string()
              .matches(/\d/, FORM_ERROR_MESSAGES.INVALID_ENTRY)
              .test('Should not contain special character', (value, context) => {
                const path = context.path;
                return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
              })
          }),

        houseOrBuildingNumber: yup.string()
          .when('quickLookup', {
            is: false,
            then: yup.string()
              .matches(/^(\d+-?)+\d+|\d$/, FORM_ERROR_MESSAGES.INVALID_ENTRY)
              .test('Should not contain special character', (value, context) => {
                const path = context.path;
                return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
              })
          }),

        buildingName: yup.string()
          .when('quickLookup', {
            is: false,
            then: yup.string()
              .matches(lettersOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
              .test('Should not contain special character', (value, context) => {
                const path = context.path;
                return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
              })
          }),
        streetName: yup.string()
          .when(['poBox', 'quickLookup'], {
            is: (poBox, quickLookup) => {
              return quickLookup == false && !(poBox && poBox.length > 0);
            },
            then: yup.string()
              .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
              .matches(lettersOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
              .test('Should not contain special character', (value, context) => {
                const path = context.path;
                return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
              }),
          }),
        cityOrSuburb: yup.string()
          .when('quickLookup', {
            is: false,
            then: yup.string()
              .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
              .matches(numberOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
              .test('Should not contain special character', (value, context) => {
                const path = context.path;
                return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
              })
          }),
        state: yup.string()
          .when('quickLookup', {
            is: false,
            then: yup.string()
              .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD),
          }),
        postcode: yup.string()
          .when('quickLookup', {
            is: false,
            then: yup.string()
              .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
              .matches(/^\d+$/, FORM_ERROR_MESSAGES.INVALID_ENTRY),
          }),
        addressLookup: yup.string()
          .when(['country', 'quickLookup'], {
            is: (country, quickLookup) => {
              return country === 'Australia' && quickLookup;
            },
            then: yup.string()
              .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
              .test('valid address test', (value, context) => {
                const path = context.path;
                const parentValues = context.parent;
                return !isValidAddess(parentValues, value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
              })
          })
      })
    }),

  mobileNumber: yup.string()
    .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
    .min(10, FORM_ERROR_MESSAGES.INVALID_ENTRY)
    .matches(/^\+?\d*$/, FORM_ERROR_MESSAGES.INVALID_ENTRY),
  isPostalAddressDifferent: yup.string()
    .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD),
});

export const identityDetailsValidationSchema = yup.object().shape({
  auPassportDetails: yup.object()
    .when('identityType', {
      is: IDENTITY_TYPES.AU_PASSPORT,
      then: yup.object().shape({
        familyName: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
          .matches(lettersOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
          .test('Should not contain special character', (value, context) => {
            const path = context.path;
            return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
          }),
        givenNames: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
          .matches(lettersOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
          .test('Should not contain special character', (value, context) => {
            const path = context.path;
            return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
          }),
        passportNumber: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
          .matches(passportNumberRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY),
        dateOfBirth: yup.string()
          .test('date-value-test', (value, context) => {
            const dateValue = moment(value, 'YYYY-MM-DD', true);
            const path = context.path;
            if (!value) {
              return context.createError({ path, message: FORM_ERROR_MESSAGES.REQUIRED_FIELD });
            }
            if (moment(value).isBefore(minDate, 'year') || moment(value).isAfter(today)) {
              return context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY });
            }
            return dateValue.isValid() || context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY });
          }).required(FORM_ERROR_MESSAGES.REQUIRED_FIELD),
        declaration: yup.string()
          .required(FORM_ERROR_MESSAGE_ACCEPT_DECLARATION),
        hasUploadedFiles: yup.boolean()
          .when('isManualUpload', {
            is: true,
            then: yup.boolean()
              .oneOf([true], 'Please upload a file')
          }),
      })
    }),
  auBirthCertificateDetails: yup.object()
    .when('identityType', {
      is: IDENTITY_TYPES.AU_BIRTH_CERTIFICATE,
      then: yup.object().shape({
        familyName: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
          .matches(lettersOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
          .test('Should not contain special character', (value, context) => {
            const path = context.path;
            return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
          }),
        givenNames: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
          .matches(lettersOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
          .test('Should not contain special character', (value, context) => {
            const path = context.path;
            return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
          }),
        stateOrTerritory: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD),
        dateOfBirth: yup.string()
          .test('date-value-test', (value, context) => {
            const dateValue = moment(value, 'YYYY-MM-DD', true);
            const path = context.path;
            if (!value) {
              return context.createError({ path, message: FORM_ERROR_MESSAGES.REQUIRED_FIELD });
            }
            if (moment(value).isBefore(minDate, 'year') || moment(value).isAfter(today)) {
              return context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY });
            }
            return dateValue.isValid() || context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY });
          }).required(FORM_ERROR_MESSAGES.REQUIRED_FIELD),
        registrationNumber: yup.string()
          .test('length-test', (value, context) => {
            const path = context.path;
            const length = value ? value.length : 0;
            if (length > 10) {
              return context.createError({ path, message: 'Must be between 1 and 10 characters' });
            }
            return true;
          })
          .matches(/\d/, FORM_ERROR_MESSAGES.INVALID_ENTRY),
        declaration: yup.string()
          .required(FORM_ERROR_MESSAGE_ACCEPT_DECLARATION),
        hasUploadedFiles: yup.boolean()
          .when('isManualUpload', {
            is: true,
            then: yup.boolean()
              .oneOf([true], 'Please upload a file')
          }),
      })
    }),
  auCitizenshipCertificateDetails: yup.object()
    .when('identityType', {
      is: IDENTITY_TYPES.AU_CITIZENSHIP_CERTIFICATE,
      then: yup.object().shape({
        familyName: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
          .matches(lettersOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
          .test('Should not contain special character', (value, context) => {
            const path = context.path;
            return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
          }),
        givenNames: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
          .matches(lettersOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
          .test('Should not contain special character', (value, context) => {
            const path = context.path;
            return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
          }),
        dateOfBirth: yup.string()
          .test('date-value-test', (value, context) => {
            const dateValue = moment(value, 'YYYY-MM-DD', true);
            const path = context.path;
            const parentValues = context.parent;
            if (!value) {
              return context.createError({ path, message: FORM_ERROR_MESSAGES.REQUIRED_FIELD });
            }
            if (moment(value).isBefore(minDate, 'year') || moment(value).isAfter(today)) {
              return context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY });
            }
            const acquisitionDate = parentValues.acquisitionDate;
            const acquisitionDateValue = moment(acquisitionDate, 'YYYY-MM-DD', true);
            if (acquisitionDateValue.isValid() && moment(value).isAfter(acquisitionDateValue)) {
              return context.createError({ path, message: 'Date of birth cannot be after the acquisition date' });
            }
            return dateValue.isValid() || context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY });
          }).required(FORM_ERROR_MESSAGES.REQUIRED_FIELD),
        acquisitionDate: yup.string()
          .test('acquisition-date-test', (value, context) => {
            const dateValue = moment(value, 'YYYY-MM-DD', true);
            const path = context.path;
            if (!value) {
              return context.createError({ path, message: FORM_ERROR_MESSAGES.REQUIRED_FIELD });
            }
            if (moment(value).isBefore(minDate, 'year') || moment(value).isAfter(today)) {
              return context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY });
            }
            return dateValue.isValid() || context.createError({ path, message: FORM_ERROR_MESSAGES.REQUIRED_FIELD });
          }).required(FORM_ERROR_MESSAGES.REQUIRED_FIELD),
        certificateNumber: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
          .matches(/\d/, FORM_ERROR_MESSAGES.INVALID_ENTRY),
        declaration: yup.string()
          .required(FORM_ERROR_MESSAGE_ACCEPT_DECLARATION),
        hasUploadedFiles: yup.boolean()
          .when('isManualUpload', {
            is: true,
            then: yup.boolean()
              .oneOf([true], 'Please upload a file')
          }),
      })
    }),
  foreignPassportDetails: yup.object()
    .when('identityType', {
      is: IDENTITY_TYPES.FORIGN_PASSPORT,
      then: yup.object().shape({
        isInAustralia: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_OPTION),
        isAuPermanentResident: yup.string()
          .when('isInAustralia', {
            is: 'no',
            then: yup.string()
              .required(FORM_ERROR_MESSAGES.REQUIRED_OPTION)
          }),
        familyName: yup.string()
          .when(['isInAustralia', 'isAuPermanentResident'], {
            is: (isInAustralia, isAuPermanentResident) => {
              return isFieldValidationRequired(isInAustralia, isAuPermanentResident);
            },
            then: yup.string()
              .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
              .matches(lettersOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
              .test('Should not contain special character', (value, context) => {
                const path = context.path;
                return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
              }),
          }),
        givenNames: yup.string()
          .when(['isInAustralia', 'isAuPermanentResident'], {
            is: (isInAustralia, isAuPermanentResident) => {
              return isFieldValidationRequired(isInAustralia, isAuPermanentResident);
            },
            then: yup.string()
              .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
              .matches(lettersOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
              .test('Should not contain special character', (value, context) => {
                const path = context.path;
                return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
              }),
          }),
        dateOfBirth: yup.string()
          .when(['isInAustralia', 'isAuPermanentResident'], {
            is: (isInAustralia, isAuPermanentResident) => {
              return isFieldValidationRequired(isInAustralia, isAuPermanentResident);
            },
            then: yup.string()
              .test('date-value-test', (value, context) => {
                const dateValue = moment(value, 'YYYY-MM-DD', true);
                const path = context.path;
                if (!value) {
                  return context.createError({ path, message: FORM_ERROR_MESSAGES.REQUIRED_FIELD });
                }
                if (moment(value).isBefore(minDate, 'year') || moment(value).isAfter(today)) {
                  return context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY });
                }
                return dateValue.isValid() || context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY });
              }).required(FORM_ERROR_MESSAGES.REQUIRED_FIELD),
          }),
        passportNumber: yup.string()
          .when(['isInAustralia', 'isAuPermanentResident'], {
            is: (isInAustralia, isAuPermanentResident) => {
              return isFieldValidationRequired(isInAustralia, isAuPermanentResident);
            },
            then: yup.string()
              .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD),
          }),
        nationalityOnPassport: yup.string()
          .when(['isInAustralia', 'isAuPermanentResident'], {
            is: (isInAustralia, isAuPermanentResident) => {
              return isFieldValidationRequired(isInAustralia, isAuPermanentResident);
            },
            then: yup.string()
              .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD),
          }),
        declaration: yup.string()
          .when(['isInAustralia', 'isAuPermanentResident'], {
            is: (isInAustralia, isAuPermanentResident) => {
              return isFieldValidationRequired(isInAustralia, isAuPermanentResident);
            },
            then: yup.string()
              .required(FORM_ERROR_MESSAGE_ACCEPT_DECLARATION),
          }),
        hasUploadedFiles: yup.boolean()
          .when('isManualUpload', {
            is: true,
            then: yup.boolean()
              .oneOf([true], 'Please upload a file')
          }),
      })
    }),
  immiCardDetails: yup.object()
    .when('identityType', {
      is: IDENTITY_TYPES.IMMI_CARD,
      then: yup.object().shape({
        familyName: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
          .matches(lettersOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
          .test('Should not contain special character', (value, context) => {
            const path = context.path;
            return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
          }),
        givenNames: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
          .matches(lettersOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
          .test('Should not contain special character', (value, context) => {
            const path = context.path;
            return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
          }),
        dateOfBirth: yup.string()
          .test('date-value-test', (value, context) => {
            const dateValue = moment(value, 'YYYY-MM-DD', true);
            const path = context.path;
            if (!value) {
              return true;
            }
            if (moment(value).isBefore(minDate, 'year') || moment(value).isAfter(today)) {
              return context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY });
            }
            return dateValue.isValid() || context.createError({ path, message: FORM_ERROR_MESSAGES_DOB_FIELD });
          }).required(FORM_ERROR_MESSAGES_DOB_FIELD),
        immiCardNumber: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
          .matches(immiCardNumberRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY),
        declaration: yup.string()
          .required(FORM_ERROR_MESSAGE_ACCEPT_DECLARATION),
        hasUploadedFiles: yup.boolean()
          .when('isManualUpload', {
            is: true,
            then: yup.boolean()
              .oneOf([true], 'Please upload a file')
          }),
      })
    })
});

export const previousNameChangeValidationSchema = yup.object().shape({
  previousName: yup.object().shape({
    nameChangeAgree: yup.string()
      .required(FORM_ERROR_MESSAGES.REQUIRED_OPTION),
    changeOfNameDocument: yup.string()
      .when('nameChangeAgree', {
        is: 'no',
        then: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_OPTION)
      })
  })
});


export const changeOfNameValidationSchema = yup.object().shape({
  changeOfNameCertificateDetails: yup.object()
    .when('previousName.changeOfNameDocument', {
      is: NAME_CHANGE_DOC_TYPES.NAME_CHANGE_CERTIFICATE,
      then: yup.object().shape({
        familyName: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
          .matches(lettersOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
          .test('Should not contain special character', (value, context) => {
            const path = context.path;
            return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
          }),
        givenNames: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
          .matches(lettersOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
          .test('Should not contain special character', (value, context) => {
            const path = context.path;
            return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
          }),
        previousGivenNames: yup.string()
          .notRequired()
          .matches(lettersOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
          .test('Should not contain special character', (value, context) => {
            const path = context.path;
            return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
          }),
        previousFamilyName: yup.string()
          .notRequired()
          .matches(lettersOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
          .test('Should not contain special character', (value, context) => {
            const path = context.path;
            return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
          }),
        dateOfBirth: yup.string()
          .test('date-value-test', (value, context) => {
            const dateValue = moment(value, 'YYYY-MM-DD', true);
            const path = context.path;
            if (!value) {
              return true;
            }
            if (moment(value).isBefore(minDate, 'year') || moment(value).isAfter(today)) {
              return context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY });
            }
            return dateValue.isValid() || context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY });
          }).required(FORM_ERROR_MESSAGES_DOB_FIELD),
        registrationDate: yup.string()
          .notRequired()
          .test('date-value-test', (value, context) => {
            const dateValue = moment(value, 'YYYY-MM-DD', true);
            const path = context.path;
            if (!value) {
              return true;
            }
            if (moment(value).isBefore(minDate, 'year') || moment(value).isAfter(today)) {
              return context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY });
            }
            return dateValue.isValid() || context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY });
          }),
        registrationYear: yup.string()
          .test('rego-year-test', (value, context) => {
            const currentYear = new Date().getFullYear();
            if (value) {
              const yearValue = value ? parseInt(value) : 0;
              const path = context.path;
              return yearValue && (yearValue >= 1900 && yearValue <= currentYear) || context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY });
            }
            return true;
          }),
        registrationNumber: yup.string()
          .required(MESSAGE_REGISTRATION_NUMBER)
          .matches(/\d/, FORM_ERROR_MESSAGES.INVALID_ENTRY),
        registrationState: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_OPTION),
        certificateIssueDate: yup.string()
          .notRequired()
          .test('date-value-test', (value, context) => {
            const dateValue = moment(value, 'YYYY-MM-DD', true);
            const path = context.path;
            if (!value) {
              return true;
            }
            if (moment(value).isBefore(minDate, 'year') || moment(value).isAfter(today)) {
              return context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY });
            }
            return dateValue.isValid() || context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY });
          }),
        declaration: yup.string()
          .required(FORM_ERROR_MESSAGE_ACCEPT_DECLARATION),
        hasUploadedFiles: yup.boolean()
          .when('isManualUpload', {
            is: true,
            then: yup.boolean()
              .oneOf([true], 'Please upload a file')
          }),
      })
    }),
  marriageCertificateDetails: yup.object()
    .when('previousName.changeOfNameDocument', {
      is: NAME_CHANGE_DOC_TYPES.MARRIAGE_CERTIFICATE,
      then: yup.object().shape({
        firstPersonFamilyName: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
          .matches(lettersOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
          .test('Should not contain special character', (value, context) => {
            const path = context.path;
            return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
          }),
        firstPersonGivenNames: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
          .matches(lettersOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
          .test('Should not contain special character', (value, context) => {
            const path = context.path;
            return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
          }),
        secondPersonFamilyName: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
          .matches(lettersOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
          .test('Should not contain special character', (value, context) => {
            const path = context.path;
            return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
          }),
        secondPersonGivenNames: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_FIELD)
          .matches(lettersOnlyRegex, FORM_ERROR_MESSAGES.INVALID_ENTRY)
          .test('Should not contain special character', (value, context) => {
            const path = context.path;
            return containsSpecialCharacters(value) ? context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY }) : true;
          }),
        dateOfMarriage: yup.string()
          .test('date-value-test', (value, context) => {
            const dateValue = moment(value, 'YYYY-MM-DD', true);
            const path = context.path;
            if (!value) {
              return true;
            }
            if (moment(value).isBefore(minDate, 'year') || moment(value).isAfter(today)) {
              return context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY });
            }
            return dateValue.isValid() || context.createError({ path, message: FORM_ERROR_MESSAGES.REQUIRED_FIELD });
          }).required(FORM_ERROR_MESSAGES.REQUIRED_FIELD),
        registrationNumber: yup.string()
          .required(MESSAGE_REGISTRATION_NUMBER)
          .matches(/\d/, MESSAGE_REGISTRATION_NUMBER),
        registrationState: yup.string()
          .required(FORM_ERROR_MESSAGES.REQUIRED_OPTION),
        declaration: yup.string()
          .required(FORM_ERROR_MESSAGE_ACCEPT_DECLARATION),
        hasUploadedFiles: yup.boolean()
          .when('isManualUpload', {
            is: true,
            then: yup.boolean()
              .oneOf([true], 'Please upload a file')
          }),
        registrationDate: yup.string()
          .notRequired()
          .test('date-value-test', (value, context) => {
            const dateValue = moment(value, 'YYYY-MM-DD', true);
            const path = context.path;
            if (!value) {
              return true;
            }
            if (moment(value).isBefore(minDate, 'year') || moment(value).isAfter(today)) {
              return context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY });
            }
            return dateValue.isValid() || context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY });
          }),
        certificateIssueDate: yup.string()
          .notRequired()
          .test('date-value-test', (value, context) => {
            const dateValue = moment(value, 'YYYY-MM-DD', true);
            const path = context.path;
            if (!value) {
              return true;
            }
            if (moment(value).isBefore(minDate, 'year') || moment(value).isAfter(today)) {
              return context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY });
            }
            return dateValue.isValid() || context.createError({ path, message: FORM_ERROR_MESSAGES.INVALID_ENTRY });
          }),
      })
    }),
  divorcePapersDetails: yup.object()
    .when('previousName.changeOfNameDocument', {
      is: NAME_CHANGE_DOC_TYPES.DIVORCE_PAPERS,
      then: yup.object().shape({
        hasUploadedFiles: yup.boolean()
          .oneOf([true], 'Please upload a file')
      })
    })
});
